import useAuth from '~/composables/useAuth'

type Layout = 'default' | 'auth'
export type AuthMode = 'auto' | 'required' | 'disabled'

const authModeStateKey = 'layout-auth-mode'
const authCallbackPath = '/auth/callback'

const isAuthMode = (value: string): value is AuthMode => {
  return value === 'auto' || value === 'required' || value === 'disabled'
}

const resolveConfiguredAuthMode = (value: string | undefined): AuthMode => {
  if (!value) {
    return 'auto'
  }

  return isAuthMode(value) ? value : 'auto'
}

const resolveEffectiveAuthMode = (
  mode: AuthMode,
  isProduction: boolean,
): Exclude<AuthMode, 'auto'> => {
  if (mode === 'auto') {
    return isProduction ? 'required' : 'disabled'
  }

  return mode
}

export const useLayoutState = () => {
  const runtimeConfig = useRuntimeConfig()
  const route = useRoute()
  const { ensureSession, user } = useAuth()

  const configuredAuthMode = resolveConfiguredAuthMode(runtimeConfig.public.AUTH_MODE)
  const authMode = useState<AuthMode>(authModeStateKey, () => configuredAuthMode)

  const isProduction = computed(() => runtimeConfig.public.APP_MODE === 'production')
  const isAuthCallbackRoute = computed(() => route.path === authCallbackPath)
  const effectiveAuthMode = computed(() => {
    return resolveEffectiveAuthMode(authMode.value, isProduction.value)
  })
  const isAuthorized = computed(() => Boolean(user.value))
  const shouldAuthorize = computed(() => {
    return effectiveAuthMode.value === 'required' && !isAuthCallbackRoute.value
  })

  const layout = computed<Layout>(() => {
    if (isAuthCallbackRoute.value) {
      return 'auth'
    }

    if (shouldAuthorize.value && !isAuthorized.value) {
      return 'auth'
    }

    return 'default'
  })

  const setAuthMode = (mode: AuthMode) => {
    authMode.value = mode
  }

  const resetAuthMode = () => {
    authMode.value = configuredAuthMode
  }

  const requireAuth = async (enforce: boolean = false) => {
    if (!enforce && !shouldAuthorize.value) {
      console.info('Skipping authentication check as authorization is not required...')
      return
    }

    console.info('Performing authentication check...')

    await ensureSession()
  }

  return {
    authMode,
    effectiveAuthMode,
    isAuthCallbackRoute,
    isAuthorized,
    layout,
    requireAuth,
    resetAuthMode,
    setAuthMode,
    shouldAuthorize,
  }
}
